import React, { useState, useEffect } from "react";
import PhysioCard from "../components/PhysioCard";
import { Loader2, MapPin, Info } from "lucide-react";

interface Physio {
  id: string;
  firstName: string;
  lastName: string;
  address: string;
  city: string;
  specialties: string[];
  isCertified: boolean;
  phone: string;
  profileType: "basic" | "filled" | "certified";
  source?: "platform" | "rpps";
  distance?: number | null;
}

interface SearchMetadata {
  expanded?: boolean;
  radius_km?: number;
  original_query?: string;
  filters_relaxed?: boolean;
  message?: string;
}

interface SidebarProps {
  physios: Physio[];
  searchMetadata?: SearchMetadata;
}

const Sidebar: React.FC<SidebarProps> = ({ physios, searchMetadata }) => {
  const [selectedPhysioId, setSelectedPhysioId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sortedPhysios, setSortedPhysios] = useState<Physio[]>([]);
  
  // Simulate loading
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);
  
  // Sort physios: for geographic searches we group results into ~5 km distance
  // bands (closest band first) and, within a band ("at comparable distance"),
  // show registered/complete profiles first. Then closest, then alphabetical.
  // For non-geographic searches, distance is absent so it falls back to the
  // registered/complete + alphabetical ordering.
  useEffect(() => {
    if (physios.length === 0) return;

    const BAND_KM = 5; // "distance comparable" = within the same 5 km band

    const sorted = [...physios].sort((a, b) => {
      // Closest distance band first (only when distance is available).
      if (a.distance != null && b.distance != null) {
        const bandA = Math.floor(a.distance / BAND_KM);
        const bandB = Math.floor(b.distance / BAND_KM);
        if (bandA !== bandB) return bandA - bandB;
      }

      // Within the same band: RPPS-only (non-inscrits) profiles go last
      const aIsRpps = a.source === "rpps";
      const bIsRpps = b.source === "rpps";
      if (aIsRpps && !bIsRpps) return 1;
      if (!aIsRpps && bIsRpps) return -1;

      // Filled and certified profiles first
      if (a.profileType !== "basic" && b.profileType === "basic") return -1;
      if (a.profileType === "basic" && b.profileType !== "basic") return 1;

      // Then certified before just filled
      if (a.profileType === "certified" && b.profileType === "filled") return -1;
      if (a.profileType === "filled" && b.profileType === "certified") return 1;

      // Equal tier: closest first, then alphabetical by lastname
      if (a.distance != null && b.distance != null && a.distance !== b.distance) {
        return a.distance - b.distance;
      }
      return a.lastName.localeCompare(b.lastName);
    });

    setSortedPhysios(sorted);
  }, [physios]);
  
  const handleCardClick = (id: string) => {
    setSelectedPhysioId(id);
    
    // Scroll selected physio into view if needed
    const element = document.getElementById(`physio-${id}`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  };
  
  if (isLoading) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-6 border-l border-gray-200 bg-white">
        <Loader2 className="h-10 w-10 animate-spin text-green-600 mb-4" />
        <p className="text-center text-gray-500">
          Chargement des kinésithérapeutes...
        </p>
      </div>
    );
  }
  
  if (physios.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-6 border-l border-gray-200 bg-white">
        <div className="text-center max-w-md">
          <div className="mb-4">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Aucun résultat pour cette recherche
          </h3>
          {searchMetadata?.message ? (
            <p className="text-gray-600 mb-4">{searchMetadata.message}</p>
          ) : (
            <p className="text-gray-600 mb-4">
              Nous n'avons trouvé aucun kinésithérapeute correspondant à vos critères de recherche.
            </p>
          )}
          <p className="text-sm text-gray-500">
            Essayez d'élargir votre recherche en modifiant les filtres ou en changeant la localisation.
          </p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="h-full flex flex-col border-l border-gray-200 bg-white overflow-hidden">
      <div className="p-4 border-b border-gray-100 flex-shrink-0">
        <div className="flex items-center justify-between">
          <h2 className="font-medium text-gray-900">
            {physios.length} kinésithérapeutes
          </h2>
          <div className="flex items-center gap-2">
            {physios.some(p => p.source === "rpps") && (
              <span className="text-xs text-gray-400 bg-gray-50 px-2 py-1 rounded-full">
                {physios.filter(p => p.source === "rpps").length} annuaire
              </span>
            )}
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
              {physios.filter(p => p.source !== "rpps").length} inscrits
            </span>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {searchMetadata?.expanded && searchMetadata.message && (
          <div className="mx-3 mt-3 mb-1 flex items-start gap-2 p-2.5 bg-blue-50 border border-blue-200 rounded-lg">
            <MapPin className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm text-blue-700">{searchMetadata.message}</p>
              {searchMetadata.filters_relaxed && (
                <p className="text-xs text-blue-500 mt-1 flex items-center gap-1">
                  <Info className="h-3 w-3" />
                  Certains filtres ont été assouplis pour afficher des résultats.
                </p>
              )}
            </div>
          </div>
        )}
        {sortedPhysios.map((physio) => (
          <div
            key={physio.id}
            id={`physio-${physio.id}`}
            className="transition-colors"
          >
            <PhysioCard 
              physio={physio} 
              isSelected={selectedPhysioId === physio.id}
              onClick={() => handleCardClick(physio.id)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default Sidebar; 
